/**
 * SharedPedigreePage
 * ------------------
 * Public, unauthenticated view of a hypothetical pedigree shared by token.
 * Rendered outside ProtectedRoute and outside Layout, so there is no app chrome
 * and no login is required — the token in the URL is the only access control.
 *
 * The document itself is <PedigreePrintout>, the same component the in-app page
 * prints, so the two cannot drift.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import PedigreePrintout from '../components/PedigreePrintout';
import {
  PedigreePrintStyles,
  PrintWatermark,
  pedigreePrintSx,
} from '../components/PedigreePrintLayer';
import { publicAPI } from '../services/api';

export default function SharedPedigreePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicAPI
      .getSharedPedigree(token)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        // Unknown, revoked, and un-renderable all present the same way — there is
        // nothing useful or safe to tell an anonymous visitor beyond "it's gone".
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading pedigree…
        </Typography>
      </Container>
    );
  }

  if (notFound || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          This link is no longer available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The pedigree link you followed has expired or been revoked. Please ask
          whoever sent it for a new one.
        </Typography>
      </Container>
    );
  }

  const shared = data.shared || {};

  return (
    <Container maxWidth="xl" sx={{ py: 4, ...pedigreePrintSx }}>
      <PedigreePrintStyles />
      <PrintWatermark />

      {/* Floated clear of the document so it does not affect its layout. */}
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          variant="outlined"
          size="small"
        >
          Print / PDF
        </Button>
      </Box>

      <PedigreePrintout data={data} title={shared.title} note={shared.note} />
    </Container>
  );
}
